import * as pbi from 'powerbi-client';
import { Constants } from '../../../constants/constants';
import { Component, ElementRef, Input, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { SlicerDto } from '../../../dto/powerbi/slicer.dto';

// Declaración para TypeScript
declare const FB: any;

@Component({
  selector: 'app-power-bi-embed',
  templateUrl: './power-bi-embed.component.html',
  styleUrls: ['./power-bi-embed.component.scss'],
})
export class PowerBiEmbedComponent implements OnInit, AfterViewInit {
  @ViewChild('reportContainer', { static: true }) reportContainer!: ElementRef;
  @Input() embedUrl!: string; // URL de incrustación del reporte
  @Input() embebedToken!: string; // Token de acceso para el reporte
  @Input() reportId!: string; // ID del reporte
  @Input() groupId!: string; // ID del grupo (Workspace)
  @Input() accessToken!: string; // ID access token propositos generales powerbi
  @Input() heigth: string = '800px'; // Altura del reporte
  @Input() isZoomEnabled: boolean = false; // Habilitar/Desabilitar el zoom
  @Input() isMobile: boolean = false; // Habilitar/Desabilitar modo movil

  mailtoLink: string = '';
  report!: pbi.Report;
  zoomLevel: number = 100; // Zoom inicial al 100%
  currentUrl: string = window.location.href;

  ngOnInit(): void {
    this.embedReport();
    this.generateMailtoLink();
  }

  ngAfterViewInit() {
    this.initializeFacebookSDK();
  }

  private initializeFacebookSDK() {
    if (typeof FB !== 'undefined') {
      this.renderFacebookButton();
      return;
    }

    this.waitForFacebookSDK().then(() => {
      this.renderFacebookButton();
    }).catch(error => {
      console.error('Error loading Facebook SDK:', error);
    });
  }

  private waitForFacebookSDK(): Promise<void> {
    return new Promise((resolve, reject) => {
      const maxAttempts = 10;
      let attempts = 0;

      const checkInterval = setInterval(() => {
        attempts++;
        if (typeof FB !== 'undefined') {
          clearInterval(checkInterval);
          resolve();
        } else if (attempts >= maxAttempts) {
          clearInterval(checkInterval);
          reject(new Error('Facebook SDK not loaded after maximum attempts'));
        }
      }, 200);
    });
  }

  private renderFacebookButton() {
    try {
      if (typeof FB !== 'undefined') {
        FB.XFBML.parse(); 
      }
    } catch (error) {
      console.error('Error rendering Facebook button:', error);
    }
  }

  generateMailtoLink() {
    const subject = encodeURIComponent(Constants.MAIL_SUBJECT);
    const body = encodeURIComponent(`${Constants.MAIL_BODY}:
  ${this.embedUrl}`);
    this.mailtoLink = `mailto:?subject=${subject}&body=${body}`;
  }

  embedReport() {
    const isMob = window.innerWidth <= 768 && this.isMobile;
    const embedConfiguration: pbi.IEmbedConfiguration = {
      type: 'report', // Tipo de incrustación
      id: this.reportId,
      embedUrl: this.embedUrl,
      accessToken: this.embebedToken,
      permissions: pbi.models.Permissions.All,
      tokenType: pbi.models.TokenType.Embed, // Tipo de token (Embed)
      settings: {
        layoutType: isMob ? pbi.models.LayoutType.MobilePortrait : pbi.models.LayoutType.Custom,
        filterPaneEnabled: false, // Mostrar/ocultar panel de filtros
        navContentPaneEnabled: false, // Mostrar/ocultar panel de navegación
      },
      pageView: 'fitToWidth', // Ajustar el reporte al ancho del contenedor
    };

    const powerbiService = new pbi.service.Service(
      pbi.factories.hpmFactory,
      pbi.factories.wpmpFactory,
      pbi.factories.routerFactory
    );

    // Incrustar el reporte en el contenedor del DOM
    this.report = powerbiService.embed(this.reportContainer.nativeElement, embedConfiguration) as pbi.Report;
  }

  onZoomChange(event: Event) {
    if (!this.report || !this.isZoomEnabled) {
      return;
    }
    const target = event.target as HTMLInputElement | null;
    if (target && this.report) {
      const zoomValue = parseInt(target.value) / 100; // Convierte el valor del slider a porcentaje
      this.report.setZoom(zoomValue); // Llama a setZoom en la instancia del reporte
      const zoomValueRounded = Math.round(zoomValue * 100);
      this.zoomLevel = zoomValueRounded; // Actualiza el valor del zoom en la vista
    }
  }

  resetZoom() {
    if (!this.report || !this.isZoomEnabled) {
      return;
    }
    this.report.setZoom(1); // Restablece el zoom al 100%
    this.zoomLevel = 100; // Actualiza el valor del zoom en la vista
  }
  
  decreaseZoom() {
    if (!this.report || !this.isZoomEnabled) {
      return;
    }
    const zoomValue = this.zoomLevel/100 - 0.1; // Disminuye el zoom en 10%
    this.report.setZoom(zoomValue); // Llama a setZoom en la instancia del reporte
    const zoomValueRounded = Math.round(zoomValue * 100);
    this.zoomLevel = zoomValueRounded; // Actualiza el valor del zoom en la vista
  }

  increaseZoom() {
    if (!this.report || !this.isZoomEnabled) {
      return;
    }
    const zoomValue = this.zoomLevel/100 + 0.1; // Aumenta el zoom en 10%
    this.report.setZoom(zoomValue); // Llama a setZoom en la instancia del reporte
    const zoomValueRounded = Math.round(zoomValue * 100);
    this.zoomLevel = zoomValueRounded; // Actualiza el valor del zoom en la vista
  }

  share(platform: string) {
    const url = encodeURIComponent(this.currentUrl);
    switch (platform) {
      case 'x':
        window.open(`https://twitter.com/share?url=${url}`, '_blank');
        break;
      case 'facebook':
        // Si el SDK está cargado, usarlo
        if (typeof FB !== 'undefined') {
          FB.ui({
            method: 'share',
            href: this.currentUrl,
          }, (response: any) => {});
        } else {
          // Fallback al método básico
          window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
        }
        break;
      case 'instagram':
        alert('Instagram no soporta compartir enlaces directamente.');
        break;
    }
  }

  async getReportSlicers(): Promise<SlicerDto[]> {
    if (!this.report) {
      return [];
    }
    try {
      const pages = await this.report.getPages();
      const activePage = pages.find(page => page.isActive) || pages[0];      
      if (!activePage) {
        console.warn('No active page found in the report.');
        return [];
      }
      const visuals = await activePage.getVisuals();
      const slicerVisuals = visuals.filter(v => v.type === 'slicer');
      const slicerStates = await Promise.all(
        slicerVisuals.map(async (slicer) => {
          const state = await slicer.getSlicerState();
          return {
            name: slicer.name,
            title: slicer.title || '',
            state: {
              filters: state.filters || []
            }
          } as SlicerDto;
        })
      );
      return slicerStates;
    } catch (error) {
      console.error('Error al obtener los slicers:', error);
      return [];
    }
  }
}